import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  Mutation,
  MutationAddRestApiKeyArgs,
  MutationDeleteRestApiKeyArgs,
  MutationUpdateRestApiKeyArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ADD_REST_API_KEY, DELETE_REST_API_KEY, UPDATE_REST_API_KEY } from 'graphql/mutations';
import { GET_REST_API_KEYS } from 'graphql/queries';
import { useEffect } from 'react';
import { deleteRestApiKeyAction, setRestApiKeysAction, updateRestApiKeyAction } from 'state/actions/restApiKeys';
import { restApiKeysVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_REST_API_KEYS_TYPED: TypedDocumentNode<Pick<Query, 'getRestApiKeys'>> = GET_REST_API_KEYS;
const ADD_REST_API_KEY_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addRestApiKey'>,
  MutationAddRestApiKeyArgs
> = ADD_REST_API_KEY;
const UPDATE_REST_API_KEY_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateRestApiKey'>,
  MutationUpdateRestApiKeyArgs
> = UPDATE_REST_API_KEY;
const DELETE_REST_API_KEY_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteRestApiKey'>,
  MutationDeleteRestApiKeyArgs
> = DELETE_REST_API_KEY;

const useRestApiKey = ({ handleFetchError }: BaseHookProps) => {
  const restApiKeys = useReactiveVar(restApiKeysVar);

  const [getRestApiKeysQuery, { loading }] = useLazyQuery(GET_REST_API_KEYS_TYPED, {
    fetchPolicy: 'network-only'
  });

  const fetchRestApiKeys = async () => {
    try {
      const { data } = await getRestApiKeysQuery();
      if (data) {
        setRestApiKeysAction(data.getRestApiKeys);
      }
    } catch {
      handleFetchError('Error while fetching API keys');
    }
  };

  useEffect(() => {
    void fetchRestApiKeys();
  }, [getRestApiKeysQuery]);

  const [addRestApiKeyMutation, { loading: addRestApiKeyLoading }] = useMutation(ADD_REST_API_KEY_TYPED, {
    onCompleted: (data) => data,
    onError: () => {
      handleFetchError('Error while adding rest API key');
    }
  });

  const addRestApiKey = async (description: string) => {
    const { data } = await ignoreHandledMutationError(
      addRestApiKeyMutation({
        variables: { description }
      })
    );
    if (data) {
      void fetchRestApiKeys();
    }
    return data;
  };

  const [updateRestApiKeyMutation, { loading: updateRestApiKeyLoading }] = useMutation(UPDATE_REST_API_KEY_TYPED, {
    onCompleted: (data) => data,
    onError: () => handleFetchError('Error while updating rest API key')
  });

  const updateRestApiKey = async (id: string, description: string) => {
    const { data } = await ignoreHandledMutationError(
      updateRestApiKeyMutation({
        variables: { id, description }
      })
    );
    if (data) {
      updateRestApiKeyAction(data.updateRestApiKey);
    }
    return data;
  };

  const [deleteRestApiKeyMutation, { loading: deleteRestApiKeyLoading }] = useMutation(DELETE_REST_API_KEY_TYPED, {
    onCompleted: (data) => data,
    onError: () => handleFetchError('Error while deleting rest API key')
  });

  const deleteRestApiKey = async (id: string) => {
    const { data } = await ignoreHandledMutationError(
      deleteRestApiKeyMutation({
        variables: { id }
      })
    );
    if (data) {
      deleteRestApiKeyAction(data.deleteRestApiKey);
    }
    return data;
  };

  return {
    restApiKeys,
    loading,
    addRestApiKey,
    addRestApiKeyLoading,
    updateRestApiKey,
    updateRestApiKeyLoading,
    deleteRestApiKey,
    deleteRestApiKeyLoading
  };
};

export default useRestApiKey;
